/** OAuth PKCE 認証ユーティリティ */

type AuthConfig = {
  /** Cognito ドメイン */
  domain: string;
  /** Cognito クライアント ID */
  clientId: string;
  /** リダイレクト URI */
  redirectUri: string;
};

/** 認証設定を遅延評価で取得する */
const getAuthConfig = (): AuthConfig => ({
  domain: import.meta.env.VITE_COGNITO_DOMAIN as string,
  clientId: import.meta.env.VITE_COGNITO_CLIENT_ID as string,
  redirectUri: `${window.location.origin}/auth/callback`,
});

/** インメモリトークンストア（モジュール外から直接変更不可） */
const createTokenStore = () => {
  let idToken: string | null = null;
  let accessToken: string | null = null;

  return {
    getIdToken: () => idToken,
    getAccessToken: () => accessToken,
    setTokens: (tokens: { idToken: string; accessToken: string }) => {
      idToken = tokens.idToken;
      accessToken = tokens.accessToken;
    },
    clear: () => {
      idToken = null;
      accessToken = null;
    },
  } as const;
};

export const tokenStore = createTokenStore();

/** Base64URL エンコード */
const base64UrlEncode = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
};

/** ランダムな 64 バイトの Base64URL 文字列を生成する */
const generateCodeVerifier = (): string => {
  const buffer = new Uint8Array(64);
  crypto.getRandomValues(buffer);
  return base64UrlEncode(buffer.buffer);
};

/** コードベリファイアの SHA-256 ハッシュを Base64URL で返す */
const generateCodeChallenge = async (verifier: string): Promise<string> => {
  const encoded = new TextEncoder().encode(verifier);
  const hash = await crypto.subtle.digest("SHA-256", encoded);
  return base64UrlEncode(hash);
};

/** Cognito 認可エンドポイントの URL を生成する */
const getLoginUrl = (codeChallenge: string): string => {
  const config = getAuthConfig();
  const params = new URLSearchParams({
    response_type: "code",
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: "openid email profile",
    identity_provider: "Google",
    code_challenge_method: "S256",
    code_challenge: codeChallenge,
  });
  return `${config.domain}/oauth2/authorize?${params.toString()}`;
};

type TokenResponse = {
  id_token: string;
  access_token: string;
  refresh_token: string;
};

/** Cognito トークンエンドポイントに code を送信してトークンを取得する */
const exchangeCodeForTokens = async (
  code: string,
  codeVerifier: string,
): Promise<void> => {
  const config = getAuthConfig();
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    code,
    code_verifier: codeVerifier,
  });

  const res = await fetch(`${config.domain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });

  if (!res.ok) {
    throw new Error(`トークン取得に失敗しました: ${res.status}`);
  }

  const tokens = (await res.json()) as TokenResponse;
  tokenStore.setTokens({
    idToken: tokens.id_token,
    accessToken: tokens.access_token,
  });
  localStorage.setItem("refresh_token", tokens.refresh_token);
};

/**
 * トークンリフレッシュの結果。
 * rejected は再ログインが必要、unreachable は通信できなかっただけで refresh_token はまだ有効な可能性がある。
 */
type RefreshResult = "refreshed" | "rejected" | "unreachable";

/** 実行中のリフレッシュ（同時に呼ばれても Cognito へのリクエストを 1 本にまとめる） */
let inflightRefresh: Promise<RefreshResult> | null = null;

/** リフレッシュトークンを使って新しいトークンを取得する */
const refreshTokens = (): Promise<RefreshResult> => {
  if (!inflightRefresh) {
    inflightRefresh = requestTokenRefresh().finally(() => {
      inflightRefresh = null;
    });
  }
  return inflightRefresh;
};

/** refresh_token を保持しているかを返す */
const hasRefreshToken = (): boolean =>
  localStorage.getItem("refresh_token") !== null;

/**
 * メモリに ID トークンがなく refresh_token がある場合、復元が終わるまで待つ。
 * 起動時は描画を止めずにリフレッシュを裏で走らせるため、API 呼び出し側でここを待つ。
 */
const restoreIdToken = async (): Promise<void> => {
  if (tokenStore.getIdToken() === null && hasRefreshToken()) {
    await refreshTokens();
  }
};

const requestTokenRefresh = async (): Promise<RefreshResult> => {
  const refreshToken = localStorage.getItem("refresh_token");
  if (!refreshToken) {
    return "rejected";
  }

  try {
    const config = getAuthConfig();
    const body = new URLSearchParams({
      grant_type: "refresh_token",
      client_id: config.clientId,
      refresh_token: refreshToken,
    });

    const res = await fetch(`${config.domain}/oauth2/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    });

    // 5xx / 429 も rejected 扱いにしている（Cognito 障害時もログインへ遷移する）。
    // 頻度が低いため見送り: https://github.com/galactic1969/payload-alpha/issues/177
    if (!res.ok) {
      return "rejected";
    }

    const tokens = (await res.json()) as Omit<TokenResponse, "refresh_token">;
    tokenStore.setTokens({
      idToken: tokens.id_token,
      accessToken: tokens.access_token,
    });
    return "refreshed";
  } catch {
    // オフラインや Cognito に到達できない場合。トークンが無効になったとは限らない
    return "unreachable";
  }
};

/** 現在のIDトークンを返す */
const getIdToken = (): string | null => tokenStore.getIdToken();

/** トークンをクリアしてログアウトする */
const logout = (): void => {
  tokenStore.clear();
  localStorage.removeItem("refresh_token");
};

/** コードベリファイアを生成して sessionStorage に保存し、ログイン画面にリダイレクトする */
const startLogin = async (): Promise<void> => {
  const verifier = generateCodeVerifier();
  sessionStorage.setItem("code_verifier", verifier);
  const challenge = await generateCodeChallenge(verifier);
  const url = getLoginUrl(challenge);
  window.location.href = url;
};

/** 認証済みかどうかを返す */
const isAuthenticated = (): boolean => tokenStore.getIdToken() !== null;

export type { RefreshResult };

export {
  getAuthConfig,
  generateCodeVerifier,
  generateCodeChallenge,
  base64UrlEncode,
  getLoginUrl,
  exchangeCodeForTokens,
  refreshTokens,
  hasRefreshToken,
  restoreIdToken,
  getIdToken,
  logout,
  startLogin,
  isAuthenticated,
};
